import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import MetricCard from './MetricCard'

export default function Dashboard() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // Format currency values: over 1M shows as "M", otherwise "k"
  const formatCurrency = (value, decimals = 1) => {
    // Handle null, undefined, or non-numeric values
    if (value == null || isNaN(value)) {
      return '$0'
    }

    const numValue = parseFloat(value)
    if (numValue >= 1000000) {
      return `$${(numValue / 1000000).toFixed(decimals)}M`
    } else if (numValue >= 1000) {
      return `$${(numValue / 1000).toFixed(decimals)}k`
    } else {
      return `$${numValue.toFixed(0)}`
    }
  }

  useEffect(() => {
    fetch('/api/dashboard')
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch dashboard data')
        return response.json()
      })
      .then(data => {
        setData(data)
        setLoading(false)
      })
      .catch(err => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">Error: {error}</div>
      </div>
    )
  }

  return (
    <div className="px-4 sm:px-0">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Dashboard</h1>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {/* Current Inventory */}
        <MetricCard
          title="Current Inventory"
          value={data.current_inventory.count}
          subtitle={`${formatCurrency(data.current_inventory.net_equity, 0)} Net Equity`}
          linkTo="/vans"
        />

        {/* Van Sales */}
        <MetricCard
          title="Vans Sold (1 Month)"
          value={data.van_sales.last_1_month}
          yoyChange={data.van_sales.yoy_change_1_month}
          linkTo="/metrics/van_sales"
        />
        <MetricCard
          title="Vans Sold (TTM)"
          value={data.van_sales.ttm}
          yoyChange={data.van_sales.yoy_change_ttm}
          linkTo="/metrics/van_sales"
        />
        <MetricCard
          title="Vans Sold (YTD)"
          value={data.van_sales.ytd}
          yoyChange={data.van_sales.yoy_change_ytd}
          linkTo="/metrics/van_sales_histogram"
        />

        {/* Financial Metrics (QuickBooks) */}
        <MetricCard
          title="Total Sales (1 Month)"
          value={formatCurrency(data.total_sales.last_4_weeks)}
          yoyChange={data.total_sales.yoy_change_4_weeks}
          linkTo="/metrics/total_sales"
        />
        <MetricCard
          title="Total Sales (1 Quarter)"
          value={formatCurrency(data.total_sales.last_12_weeks)}
          yoyChange={data.total_sales.yoy_change_12_weeks}
          linkTo="/metrics/total_sales"
        />
        <MetricCard
          title="Total Sales (1 Year)"
          value={formatCurrency(data.total_sales.last_52_weeks, 0)}
          yoyChange={data.total_sales.yoy_change_52_weeks}
          linkTo="/metrics/total_sales"
        />

        <MetricCard
          title="Net Income (1 Month)"
          value={formatCurrency(data.net_income.last_1_month)}
          yoyChange={data.net_income.yoy_change_1_month}
          linkTo="/metrics/net_income"
        />
        <MetricCard
          title="Net Income (1 Quarter)"
          value={formatCurrency(data.net_income.last_1_quarter)}
          yoyChange={data.net_income.yoy_change_1_quarter}
          linkTo="/metrics/net_income"
        />
        <MetricCard
          title="Net Income (YTD)"
          value={formatCurrency(data.net_income.ytd, 0)}
          yoyChange={data.net_income.yoy_change_ytd}
          linkTo="/metrics/net_income"
        />

        <MetricCard
          title="Marketing Spend (1 Month)"
          value={formatCurrency(data.marketing_spend.last_4_weeks)}
          yoyChange={data.marketing_spend.yoy_change_4_weeks}
          linkTo="/metrics/marketing_spend"
        />
        <MetricCard
          title="Marketing Spend (1 Quarter)"
          value={formatCurrency(data.marketing_spend.last_12_weeks)}
          yoyChange={data.marketing_spend.yoy_change_12_weeks}
          linkTo="/metrics/marketing_spend"
        />
        <MetricCard
          title="Marketing Spend (1 Year)"
          value={formatCurrency(data.marketing_spend.last_52_weeks, 0)}
          yoyChange={data.marketing_spend.yoy_change_52_weeks}
          linkTo="/metrics/marketing_spend"
        />

        {/* Website Visits */}
        <MetricCard
          title="Website Visits (30 days)"
          value={(data.website_visits.last_30_days / 1000).toFixed(1) + 'k'}
          yoyChange={data.website_visits.yoy_change}
          changeLabel="MoM"
          linkTo="/metrics/website_visits"
        />

        {/* Instagram Followers */}
        <MetricCard
          title="Instagram Followers"
          value={(data.instagram_followers.current / 1000).toFixed(1) + 'k'}
          yoyChange={data.instagram_followers.yoy_change}
          absoluteChange={data.instagram_followers.absolute_change}
          changeLabel="MoM"
          linkTo="/metrics/instagram_followers"
        />

        {/* Inbound Leads */}
        <MetricCard
          title="Inbound Leads (3 months)"
          value={data.inbound_leads.last_3_months}
          yoyChange={data.inbound_leads.yoy_change}
          linkTo="/metrics/inbound_leads"
        />

        {/* YouTube Views */}
        {data.youtube_video_views && (
          <MetricCard
            title="YouTube Views (30 days)"
            value={(data.youtube_video_views.last_30_days / 1000).toFixed(1) + 'k'}
            yoyChange={data.youtube_video_views.mom_change}
            changeLabel="MoM"
            linkTo="/metrics/youtube_video_views"
          />
        )}

        {/* Net Assets */}
        <MetricCard
          title="Net Assets (estimate)"
          value={formatCurrency(data.net_assets.current, 0)}
          yoyChange={data.net_assets.yoy_change}
          linkTo="/metrics/net_assets"
        />

        {/* Current Cash */}
        <MetricCard
          title="Current Cash"
          value={formatCurrency(data.current_cash.current, 0)}
          yoyChange={data.current_cash.yoy_change}
          subtitle={data.current_cash.snapshot_date ? `as of ${data.current_cash.snapshot_date}` : null}
          linkTo="/metrics/current_cash"
        />
      </div>
    </div>
  )
}
